import type {
  CatalogCompensation,
  CatalogSagaEntry,
  CatalogSagaStepEntry,
} from "@layer/dashboard/types";
import { api } from "../lib/api";
import { invalidate, useCachedResource } from "../lib/resource-cache";

/**
 * Phase 31e — render a saga's resolved retry policy on the Sagas card.
 * `null` (no override) → "default (5 attempts · ~5 min total)" hint so
 * operators see the saga isn't tuned for user-facing latency. Override
 * → "N attempts · ~Ks total" with the computed total wall-time (sum of
 * backoff_ms entries / 1000, 1 decimal).
 */
export function renderRetryPolicy(
  rp: CatalogSagaEntry["retry_policy"],
): JSX.Element {
  if (rp === null) {
    return (
      <span style={{ color: "#82858c" }}>
        default (5 attempts · ~5 min total)
      </span>
    );
  }
  const totalMs = rp.backoff_ms.reduce((a, b) => a + b, 0);
  const totalSec = (totalMs / 1000).toFixed(1);
  return (
    <span>
      <strong>{rp.attempts}</strong> attempts · ~{totalSec}s total{" "}
      <span style={{ color: "#82858c", fontSize: 11 }}>
        backoff_ms=[{rp.backoff_ms.join(", ")}]
      </span>
    </span>
  );
}

/**
 * Phase 16 — render a saga step's compensation kind on the Sagas card.
 * Mirrors `compensationLabel()` but for the narrower per-step taxonomy
 * (steps cannot declare `pending:multi-step` per `defineSaga()` gates).
 */
export function sagaStepCompensationLabel(
  c: CatalogSagaStepEntry["compensation_kind"],
): string {
  switch (c) {
    case "noop:read":
      return "no-op (read step)";
    case "noop:single-step-write":
      return "no-op (single-step write)";
    case "fn":
      return "fn (declarative)";
    case "function":
      return "function (live)";
  }
}

/**
 * Phase 12 — render the tagged-union `CatalogCompensation` as a
 * staff-readable label. `pending:multi-step` is the declared marker
 * for wrapper canonical actions whose compensation is realised by
 * per-step compensations in the saga (book_session / cancel_booking /
 * reschedule_booking); the chokepoint doesn't read it.
 */
export function compensationLabel(c: CatalogCompensation): string {
  switch (c.kind) {
    case "noop:read":
      return "no-op (read action)";
    case "noop:single-step-write":
      return "no-op (single-step write)";
    case "pending:multi-step":
      return "multi-step (per-step compensations in saga)";
    case "fn":
      return `compensation fn: ${c.fn}`;
  }
}

export function Catalog(): JSX.Element {
  // Phase 67 — cache-backed. `Refresh` invalidates the shared key so the
  // hook revalidates in the background (no blank flash on return either).
  const { data: c, error } = useCachedResource("catalog", () => api.catalog());
  const load = (): void => invalidate("catalog");

  if (error) return <div className="empty">Error: {error.message}</div>;
  if (!c) return <div className="empty">Loading…</div>;
  return (
    <div>
      <h1 className="page-title">
        Catalog <button onClick={load}>Refresh</button>
      </h1>
      <div className="panel">
        <h2>Canonical actions · {c.actions.length}</h2>
        <p>
          {c.actions.map((a) => (
            <span className="tag" key={a.name}>
              {a.name}
            </span>
          ))}
        </p>
      </div>
      <div className="panel">
        <h2>Connectors · {c.connectors.length}</h2>
        <p>
          {c.connectors.map((x) => (
            <span className="tag" key={x.name}>
              {x.name}
            </span>
          ))}
        </p>
      </div>
      <div className="panel">
        <h2>Repositories · {c.repositories.length}</h2>
        <p>
          {c.repositories.map((x) => (
            <span className="tag" key={x.name}>
              {x.name}
            </span>
          ))}
        </p>
      </div>
      <div className="panel">
        <h2>Scopes · {c.scopes.length}</h2>
        <p>
          {c.scopes.map((s) => (
            <span className="tag" key={s}>
              {s}
            </span>
          ))}
        </p>
      </div>
      <div className="panel">
        <h2>Actor kinds · {c.actor_kinds.length}</h2>
        <p>
          {c.actor_kinds.map((s) => (
            <span className="tag" key={s}>
              {s}
            </span>
          ))}
        </p>
      </div>
      <div className="panel">
        <h2>Canonical entities · {c.canonical_entities.length}</h2>
        <p>
          {c.canonical_entities.map((s) => (
            <span className="tag" key={s}>
              {s}
            </span>
          ))}
        </p>
        <h2 style={{ marginTop: "1.5rem" }}>
          Substrate · browsable for ops ·{" "}
          {c.browsable_entities.length - c.canonical_entities.length}
        </h2>
        <p>
          {c.browsable_entities
            .filter(
              (e) =>
                !(c.canonical_entities as ReadonlyArray<string>).includes(e),
            )
            .map((s) => (
              <span className="tag" key={s}>
                {s}
              </span>
            ))}
        </p>
      </div>
      <div className="panel">
        <h2>Webhook routes · {c.webhook_routes.length}</h2>
        <p>
          {c.webhook_routes.map((r, i) => (
            <span className="tag" key={i}>
              {r.method} {r.pattern}
            </span>
          ))}
        </p>
      </div>
      <div className="panel">
        <h2>Migrations · {c.migrations.length}</h2>
        <p>
          {c.migrations.map((m) => (
            <span className="tag" key={m.filename}>
              {m.filename}
            </span>
          ))}
        </p>
      </div>
      <div className="panel">
        <h2>Sagas · {c.sagas.length}</h2>
        <p style={{ fontSize: 12, color: "#5c6069", marginTop: 0 }}>
          Per-saga <code>retry_policy</code> tuned per phase 31e — user-facing
          sagas use tight ladders (sub-5s wall-time before DLQ), vendor-emitted
          background sagas keep the default 5×256s (~5 min total).
        </p>
        <table style={{ width: "100%", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
              <th>Saga</th>
              <th>Retry policy</th>
              <th>Trigger</th>
            </tr>
          </thead>
          <tbody>
            {c.sagas.map((s) => (
              <tr key={s.name} style={{ borderBottom: "1px solid #eee" }}>
                <td>
                  <code>{s.name}</code>
                </td>
                <td>{renderRetryPolicy(s.retry_policy)}</td>
                <td style={{ fontSize: 11, color: "#82858c" }}>
                  {s.trigger_event !== null ? (
                    <>
                      event <code>{s.trigger_event}</code>
                    </>
                  ) : (
                    <>wrapper canonical action</>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="panel">
        <h2>
          CLI subcommands ·{" "}
          {c.cli.nouns.reduce((acc, n) => acc + n.verbs.length, 0)}
        </h2>
        <p>
          {c.cli.nouns.flatMap((n) =>
            n.verbs.map((v) => (
              <span className="tag" key={`${n.noun}-${v.verb}`}>
                layer {n.noun} {v.verb}
              </span>
            )),
          )}
        </p>
      </div>
      {c.chat_translator !== null ? (
        <div className="panel">
          <h2>Chat Translator (phase 31c) · prompt + per-studio config</h2>
          <p>
            <strong>System prompt SHA:</strong>{" "}
            <code>{c.chat_translator.system_prompt_sha}</code>
          </p>
          <h3 style={{ marginTop: 12 }}>
            Tools the LLM has access to
          </h3>
          <p style={{ fontSize: 12, color: "#5c6069", marginTop: 0 }}>
            Catalog filtered to actions whose <code>allowed_actors</code>{" "}
            includes <code>human:member</code>, plus the synthetic{" "}
            <code>send_reply</code> tool the host injects (recipient +
            channel bound from resolver context — not exposed to the LLM).
          </p>
          {(() => {
            const memberActions = c.actions.filter((a) =>
              a.allowed_actors.includes("human:member"),
            );
            const allTools = [
              ...memberActions.map((a) => ({
                name: a.name,
                description:
                  a.agent_description ??
                  "(no agentDescription — falls back to stock string)",
                synthetic: false,
              })),
              {
                name: "send_reply",
                description:
                  "Synthetic host-injected tool. Sends a natural-language reply via WhatsApp. Body-only input — recipient + channel bound from resolver context. The LLM must call this exactly once per turn.",
                synthetic: true,
              },
            ];
            return (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: 6, width: "30%" }}>
                      Tool name
                    </th>
                    <th style={{ textAlign: "left", padding: 6 }}>
                      Description shown to LLM
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allTools.map((t) => (
                    <tr key={t.name}>
                      <td style={{ padding: 6, verticalAlign: "top" }}>
                        <code>{t.name}</code>
                        {t.synthetic ? (
                          <div style={{ fontSize: 10, color: "#90939a" }}>
                            (synthetic — host-injected)
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: 6, fontSize: 12 }}>
                        {t.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
          <details style={{ marginTop: 12 }}>
            <summary>Show base system prompt text</summary>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                fontSize: 12,
                background: "#14151a",
                color: "#d5d2cc",
                padding: 12,
                maxHeight: 360,
                overflowY: "auto",
              }}
            >
              {c.chat_translator.system_prompt_text}
            </pre>
          </details>
          <h3 style={{ marginTop: 12 }}>Per-studio config</h3>
          {c.chat_translator.studios.length === 0 ? (
            <p>
              <em>No studios configured.</em>
            </p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: 6 }}>Studio</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Brand voice</th>
                  <th style={{ textAlign: "left", padding: 6 }}>
                    Onboarding guidance
                  </th>
                  <th style={{ textAlign: "left", padding: 6 }}>
                    Rate limit / 24h
                  </th>
                </tr>
              </thead>
              <tbody>
                {c.chat_translator.studios.map((s) => (
                  <tr key={s.slug}>
                    <td style={{ padding: 6 }}>
                      <code>{s.slug}</code>
                    </td>
                    <td style={{ padding: 6, fontSize: 12 }}>
                      {s.brand_voice ?? <em>—</em>}
                    </td>
                    <td style={{ padding: 6, fontSize: 12 }}>
                      {s.onboarding_guidance ?? <em>—</em>}
                    </td>
                    <td style={{ padding: 6 }}>
                      {s.max_llm_calls_per_member_per_24h}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
      {c.marketing_flows.length > 0 ? (
        <div className="panel">
          <h2>Marketing flows · {c.marketing_flows.length}</h2>
          <p style={{ fontSize: 12, color: "#5c6069", marginTop: 0 }}>
            Loaded from <code>verticals/&lt;v&gt;/studios/&lt;slug&gt;/flows/&lt;name&gt;.yaml</code>{" "}
            at boot. Per-studio + per-flow read-only definitions. Edit the
            YAML + redeploy to change copy / delays / exit conditions.
          </p>
          {c.marketing_flows.map((flow) => (
            <div
              key={`${flow.studio_slug}-${flow.name}`}
              style={{
                marginTop: 16,
                padding: 12,
                border: "1px solid #ddd",
                borderRadius: 4,
              }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
                <h3 style={{ margin: 0 }}>
                  <code>{flow.name}</code>
                </h3>
                <span className="tag" style={{ fontSize: 11 }}>
                  studio: {flow.studio_slug}
                </span>
              </div>
              <p style={{ fontSize: 12, margin: "6px 0" }}>
                <strong>Trigger:</strong>{" "}
                <code>{flow.trigger_event}</code> ·{" "}
                <strong>Exits on:</strong>{" "}
                {flow.exit_events.length > 0 ? (
                  flow.exit_events.map((e) => (
                    <code key={e} style={{ marginRight: 4 }}>
                      {e}
                    </code>
                  ))
                ) : (
                  <em>none</em>
                )}{" "}
                · <strong>Timeout:</strong> {flow.exit_after_hours}h
              </p>
              <table
                className="data-table"
                style={{ fontSize: 12, marginTop: 8 }}
              >
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Delay (h)</th>
                    <th>Action</th>
                    <th>Opt-out check</th>
                  </tr>
                </thead>
                <tbody>
                  {flow.steps.map((step, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{step.delay_after_previous_hours}</td>
                      <td>
                        <code>{step.action}</code>
                      </td>
                      <td>
                        {step.opt_out_check ?? <em>—</em>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
